import { initDexignLabNav } from '@/utils/dlabnav-init-safe';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link, NavLink } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import toast from 'react-hot-toast';
import { db } from '@root/firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

const Header = () => {
  const { globalUser, globalData, defaultAvatarUrl, logout } = useAuth();
  const location = useLocation();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMini, setIsMini] = useState(false);

  const notifMenuRef = useRef(null);

  useEffect(() => {
    const cleanup = initDexignLabNav();
    return () => cleanup && cleanup();
  }, []);

  /* ---------- burger / sidebar mini ---------- */
  useEffect(() => {
    const main = document.querySelector('#main-wrapper');
    const burgerBtn = document.querySelector('.nav-control');
    const burgerIcon = document.querySelector('.hamburger');
    if (!main || !burgerBtn) return;

    const syncMiniState = () => {
      const mini = main.classList.contains('menu-toggle');
      setIsMini(mini);
      document.documentElement.classList.toggle('sidebar-mini', mini);
    };
    syncMiniState();

    const onClick = () => {
      main.classList.toggle('menu-toggle');
      burgerIcon && burgerIcon.classList.toggle('is-active');
      syncMiniState();
    };

    burgerBtn.addEventListener('click', onClick);
    const mo = new MutationObserver(syncMiniState);
    mo.observe(main, { attributes: true, attributeFilter: ['class'] });

    return () => {
      burgerBtn.removeEventListener('click', onClick);
      mo.disconnect();
    };
  }, [location.pathname]);

  /* ---------- notifications live ---------- */
  useEffect(() => {
    if (!globalUser?.uid) return;
    const qref = query(
      collection(db, `users/${globalUser.uid}/notifications`),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      qref,
      async (snapshot) => {
        const list = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let senderProfile = {};
            if (data.senderId) {
              try {
                const senderRef = doc(db, 'users', data.senderId);
                const senderSnap = await getDoc(senderRef);
                if (senderSnap.exists()) senderProfile = senderSnap.data();
              } catch {}
            }
            return {
              id: docSnap.id,
              ...data,
              senderName: senderProfile.name || data.senderName || 'User',
              avatar: senderProfile.avatar || data.avatar || defaultAvatarUrl,
            };
          })
        );
        setNotifications(list);
        setUnreadCount(list.filter((n) => !n.read).length);
      },
      (err) => console.error('Notifications snapshot error:', err)
    );

    return () => unsub();
  }, [globalUser?.uid, defaultAvatarUrl]);

  function formatWhen(ts) {
    try {
      let d;
      if (!ts) return '';
      if (ts.toDate) d = ts.toDate();
      else if (typeof ts === 'number') d = new Date(ts);
      else d = new Date(ts);

      const sec = Math.floor((Date.now() - d.getTime()) / 1000);
      if (sec < 60) return 'just now';
      const mins = Math.floor(sec / 60);
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d ago`;
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      }).format(d);
    } catch {
      return '';
    }
  }

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('User logged out successfully.');
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  const markNotificationAsRead = async (notifId) => {
    try {
      const notifRef = doc(
        db,
        `users/${globalUser.uid}/notifications/${notifId}`
      );
      await updateDoc(notifRef, { read: true });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notifId, senderId) => {
    try {
      await deleteDoc(
        doc(db, `users/${globalUser.uid}/notifications/${notifId}`)
      );
      if (senderId) {
        const mirrorRef = doc(
          db,
          `users/${senderId}/notifications/${globalUser.uid}`
        );
        const mirrorSnap = await getDoc(mirrorRef);
        if (mirrorSnap.exists()) await deleteDoc(mirrorRef);
      }
      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach((n) => {
        if (!n.read) {
          batch.update(
            doc(db, `users/${globalUser.uid}/notifications/${n.id}`),
            { read: true }
          );
        }
      });
      await batch.commit();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read!');
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  };

  const acceptConnectionRequest = async (notif) => {
    const receiverId = globalUser?.uid;
    const senderId = notif?.senderId;
    if (!receiverId || !senderId) return;

    try {
      const batch = writeBatch(db);
      const nowFields = { createdAt: serverTimestamp(), createdBy: receiverId };
      batch.set(doc(db, `users/${receiverId}/connections/${senderId}`), {
        ...nowFields,
      });
      batch.set(doc(db, `users/${senderId}/connections/${receiverId}`), {
        ...nowFields,
      });
      batch.delete(doc(db, `users/${receiverId}/notifications/${notif.id}`));
      const mirrorRef = doc(
        db,
        `users/${senderId}/notifications/${receiverId}`
      );
      const mirrorSnap = await getDoc(mirrorRef);
      if (mirrorSnap.exists()) batch.delete(mirrorRef);
      await batch.commit();
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      setUnreadCount((prev) => Math.max(prev - (notif.read ? 0 : 1), 0));
      toast.success('Connection request accepted');
    } catch (e) {
      console.error('accept connection error', e);
      toast.error('Failed to accept request');
    }
  };

  const declineConnectionRequest = async (notif) => {
    const receiverId = globalUser?.uid;
    const senderId = notif?.senderId;
    if (!receiverId || !senderId) return;

    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, `users/${receiverId}/connections/${senderId}`));
      batch.delete(doc(db, `users/${senderId}/connections/${receiverId}`));
      batch.delete(doc(db, `users/${receiverId}/notifications/${notif.id}`));
      const mirrorRef = doc(
        db,
        `users/${senderId}/notifications/${receiverId}`
      );
      const mirrorSnap = await getDoc(mirrorRef);
      if (mirrorSnap.exists()) batch.delete(mirrorRef);
      await batch.commit();
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      setUnreadCount((prev) => Math.max(prev - (notif.read ? 0 : 1), 0));
      toast.success('Connection request declined');
    } catch (e) {
      console.error('decline connection error', e);
      toast.error('Failed to decline request');
    }
  };

  const pageTitleMap = {
    '/app/dashboard': 'SMART Dashboard',
    '/app/profile': 'SMART Profile',
    '/app/dog': 'SMART Dog',
    '/app/library': 'SMART Library',
    '/app/articles': 'SMART Articles',
    '/app/memorial': 'SMART Memorial',
    '/app/directory': 'SMART Directory',
    '/app/question': 'SMART Question',
    '/app/calendar': 'SMART Calendar',
    '/app/admin/user/users': 'ADMINISTRATION - SMART Users',
    '/app/admin/user/participants': 'ADMINISTRATION - SMART Participants',
    '/app/admin/library': 'ADMINISTRATION - SMART Library',
    '/app/admin/articles': 'ADMINISTRATION - SMART Articles',
    '/app/admin/directory': 'ADMINISTRATION - SMART Directory',
    '/app/admin/faq': 'ADMINISTRATION - SMART FAQ',
    '/app/products': 'SMART Products',
    '/app/adverts': 'SMART Adverts',
    '/app/marketplace': 'SMART Marketplace',
    '/app/contact': 'Contact the SMART Team',
    '/app/faq': 'SMART FAQ',
    '/app/user/users': 'SMART Users',
    '/app/user/participants': 'SMART Participants',
  };
  const pageTitle = pageTitleMap[location.pathname] || 'Default Title';

  const logo = '/images/logo-blue.png';
  const logoSmall = '/images/logo-trans.png';

  useEffect(() => {
    let style = document.getElementById('sidebar-mini-link-color');
    if (!style) {
      style = document.createElement('style');
      style.id = 'sidebar-mini-link-color';
      style.textContent =
        '.sidebar-mini .dropdown-menu .dropdown-item, .sidebar-mini .dropdown-menu a { color: var(--bs-primary) !important; }';
      document.head.appendChild(style);
    }
  }, []);

  return (
    <>
      {/* Nav header */}
      <div className='nav-header'>
        <Link
          id='tour-header-logo' /* ✅ tour: logo */
          data-tour='tour-header-logo'
          to='/app/dashboard'
          className='brand-logo'
        >
          {!isMini ? (
            <div className='brand-title'>
              <img src={logo} alt='Logo' style={{ height: 250 }} />
            </div>
          ) : (
            <img src={logoSmall} alt='Logo' style={{ height: 40 }} />
          )}
        </Link>

        <div
          id='tour-burger' /* ✅ tour: burger */
          data-tour='tour-burger'
          className='nav-control'
          title='Toggle sidebar'
          aria-controls='main-wrapper'
          aria-label='Toggle sidebar'
          role='button'
          tabIndex={0}
        >
          <div className={`hamburger ${isMini ? 'is-active' : ''}`}>
            <span className='line'></span>
            <span className='line'></span>
            <span className='line'></span>
          </div>
        </div>
      </div>

      {/* Top header */}
      <div className='header border-bottom'>
        <div className='header-content'>
          <nav className='navbar navbar-expand'>
            <div className='collapse navbar-collapse justify-content-between'>
              <div className='header-left'>
                <div className='dashboard_bar'>{pageTitle}</div>
              </div>

              <ul className='navbar-nav header-right'>
                {/* Search */}
                <li className='nav-item d-flex align-items-center'>
                  <div className='input-group search-area'>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Search here...'
                    />
                    <span className='input-group-text'>
                      <button type='button' className='btn p-0'>
                        <i className='flaticon-381-search-2' />
                      </button>
                    </span>
                  </div>
                </li>

                {/* Notifications */}
                <li
                  id='tour-notifications' /* ✅ tour: notifications icon */
                  data-tour='tour-notifications'
                  className='nav-item dropdown notification_dropdown'
                >
                  <a
                    className='nav-link'
                    href='#'
                    role='button'
                    data-bs-toggle='dropdown'
                    onClick={(e) => e.preventDefault()}
                    aria-expanded='false'
                    aria-haspopup='true'
                    aria-controls='tour-notifications-menu'
                  >
                    <svg
                      width='28'
                      height='28'
                      viewBox='0 0 28 28'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M23.3333 19.8333H23.1187C23.2568 19.4597 23.3295 19.065 23.3333 18.6666V12.8333C23.3294 10.7663 22.6402 8.75902 21.3735 7.12565C20.1068 5.49228 18.3343 4.32508 16.3333 3.80679V3.49996C16.3333 2.88112 16.0875 2.28763 15.6499 1.85004C15.2123 1.41246 14.6188 1.16663 14 1.16663C13.3812 1.16663 12.7877 1.41246 12.3501 1.85004C11.9125 2.28763 11.6667 2.88112 11.6667 3.49996V3.80679C9.66574 4.32508 7.89317 5.49228 6.6265 7.12565C5.35983 8.75902 4.67058 10.7663 4.66667 12.8333V18.6666C4.67053 19.065 4.74316 19.4597 4.88133 19.8333H4.66667C4.35725 19.8333 4.0605 19.9562 3.84171 20.175C3.62292 20.3938 3.5 20.6905 3.5 21C3.5 21.3094 3.62292 21.6061 3.84171 21.8249C4.0605 22.0437 4.35725 22.1666 4.66667 22.1666H23.3333C23.6428 22.1666 23.9395 22.0437 24.1583 21.8249C24.3771 21.6061 24.5 21.3094 24.5 21C24.5 20.6905 24.3771 20.3938 24.1583 20.175C23.9395 19.9562 23.6428 19.8333 23.3333 19.8333Z'
                        fill='#717579'
                      />
                      <path
                        d='M9.98192 24.5C10.3863 25.2088 10.971 25.7981 11.6766 26.2079C12.3823 26.6178 13.1839 26.8337 13.9999 26.8337C14.816 26.8337 15.6175 26.6178 16.3232 26.2079C17.0288 25.7981 17.6135 25.2088 18.0179 24.5H9.98192Z'
                        fill='#717579'
                      />
                    </svg>
                    {unreadCount > 0 && (
                      <span className='badge light text-white bg-primary rounded-circle'>
                        {unreadCount}
                      </span>
                    )}
                  </a>

                  <div
                    id='tour-notifications-menu' /* ✅ tour: notifications dropdown */
                    data-tour='tour-notifications-menu'
                    className='dropdown-menu dropdown-menu-end'
                    ref={notifMenuRef}
                  >
                    <div
                      className='widget-media dlab-scroll p-3'
                      style={{ height: 380 }}
                    >
                      <button
                        className='btn btn-sm btn-light mb-2'
                        onClick={markAllNotificationsAsRead}
                      >
                        Mark all as read
                      </button>
                      <ul className='timeline list-unstyled m-0'>
                        {notifications.map((n) => {
                          const isConnReq = n.type === 'connection_request';
                          return (
                            <li key={n.id} className='px-3 py-2 border-bottom'>
                              <div className='d-flex align-items-start justify-content-between'>
                                <div className='me-3 flex-shrink-0'>
                                  <img
                                    alt='avatar'
                                    width='40'
                                    height='40'
                                    className='rounded'
                                    src={n.avatar || defaultAvatarUrl}
                                  />
                                </div>
                                <div className='flex-grow-1'>
                                  <div className='fw-semibold'>
                                    {n.senderName || 'User'}
                                    {isConnReq && (
                                      <span className='text-muted ms-2'>
                                        wants to connect
                                      </span>
                                    )}
                                  </div>

                                  {/* Actions */}
                                  <div className='d-flex align-items-center gap-2 mt-2'>
                                    {isConnReq ? (
                                      <>
                                        <button
                                          className='btn btn-sm btn-danger rounded-pill px-3'
                                          title='Decline'
                                          onClick={() =>
                                            declineConnectionRequest(n)
                                          }
                                        >
                                          <span
                                            className='text-white'
                                            style={{
                                              width: 34,
                                              height: 34,
                                              padding: 0,
                                            }}
                                          >
                                            ×
                                          </span>
                                        </button>
                                        <button
                                          className='btn btn-sm btn-success rounded-circle'
                                          style={{
                                            width: 34,
                                            height: 34,
                                            padding: 0,
                                          }}
                                          title='Accept'
                                          onClick={() =>
                                            acceptConnectionRequest(n)
                                          }
                                        >
                                          <span
                                            className='text-white'
                                            style={{ fontWeight: 700 }}
                                          >
                                            ✓
                                          </span>
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        {n.type && (
                                          <span className='badge bg-primary badge-xs'>
                                            {n.type}
                                          </span>
                                        )}
                                        {!n.read && (
                                          <button
                                            className='btn btn-xs btn-outline-primary'
                                            onClick={() =>
                                              markNotificationAsRead(n.id)
                                            }
                                          >
                                            Mark read
                                          </button>
                                        )}
                                        <button
                                          className='btn btn-xs btn-outline-danger'
                                          onClick={() =>
                                            deleteNotification(n.id, n.senderId)
                                          }
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className='ms-2 text-nowrap text-muted fs-12'>
                                  {formatWhen(n.createdAt)}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                        {!notifications.length && (
                          <div className='text-muted text-center py-3'>
                            No notifications
                          </div>
                        )}
                      </ul>
                    </div>
                  </div>
                </li>

                {/* Profile */}
                <li
                  id='tour-profile' /* ✅ tour: profile button */
                  data-tour='tour-profile'
                  className='nav-item dropdown header-profile'
                >
                  <a
                    className='nav-link btn btn-link p-0'
                    data-bs-toggle='dropdown'
                    href='#'
                    onClick={(e) => e.preventDefault()}
                    aria-expanded='false'
                    aria-haspopup='true'
                    aria-controls='tour-profile-menu'
                  >
                    <img
                      className='rounded-circle header-profile-user'
                      src={globalData?.avatar || defaultAvatarUrl}
                      alt='Header Avatar'
                      style={{ width: 48, height: 48, borderRadius: '50%' }}
                    />
                    <div className='header-info ms-3'>
                      <span className='fs-18 font-w500 mb-2'>
                        {globalData?.name || 'User'}
                      </span>
                      <small className='fs-12 font-w400'>
                        {globalUser?.email || 'Guest User'}
                      </small>
                    </div>
                  </a>

                  <div
                    id='tour-profile-menu' /* ✅ tour: profile dropdown */
                    data-tour='tour-profile-menu'
                    className='dropdown-menu dropdown-menu-end'
                  >
                    {[
                      {
                        name: 'SMART Profile',
                        link: '/app/profile',
                        icon: 'mdi mdi-account-circle',
                      },
                      {
                        name: 'SMART Subscription',
                        link: '/app/profile',
                        icon: 'mdi mdi-account-cash',
                      },
                      {
                        name: 'SMART FAQ',
                        link: '/app/faq',
                        icon: 'mdi mdi-head-question-outline',
                      },
                      {
                        name: 'Sign Out',
                        onClick: handleLogout,
                        icon: 'mdi mdi-logout',
                      },
                    ].map((item, i) =>
                      item.link ? (
                        <NavLink
                          key={i}
                          to={item.link}
                          className='dropdown-item ai-icon'
                        >
                          <i
                            className={`${item.icon} text-muted font-size-16 align-middle me-2`}
                          />
                          <span className='ms-2'>{item.name}</span>
                        </NavLink>
                      ) : (
                        <button
                          key={i}
                          className='dropdown-item ai-icon'
                          onClick={item.onClick}
                        >
                          <i
                            className={`${item.icon} text-muted font-size-16 align-middle me-2`}
                          />
                          <span className='ms-2'>{item.name}</span>
                        </button>
                      )
                    )}
                  </div>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
